import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { X, Camera, ImageUp, Loader2, ScanLine } from "lucide-react";

export function parseCfdiQr(text) {
  if (!text) return null;
  const uuidMatch = text.match(/[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}/);
  if (!uuidMatch) return null;
  const get = (key) => {
    const m = text.match(new RegExp(`[?&]${key}=([^&]*)`, "i"));
    return m ? decodeURIComponent(m[1]) : "";
  };
  const ttRaw = get("tt");
  let total = null;
  if (ttRaw) {
    const parsed = parseFloat(ttRaw.replace(/^0+(?=\d)/, ""));
    if (!Number.isNaN(parsed)) total = parsed;
  }
  return {
    uuid: uuidMatch[0].toUpperCase(),
    total,
    rfc_emisor: get("re"),
    rfc_receptor: get("rr"),
    raw: text,
  };
}

export default function QrCfdiScanner({ onResult, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const doneRef = useRef(false);
  const [cameraError, setCameraError] = useState(false);
  const [notCfdi, setNotCfdi] = useState(false);
  const [decoding, setDecoding] = useState(false);

  const handleText = (text) => {
    const parsed = parseCfdiQr(text);
    if (parsed) {
      if (doneRef.current) return;
      doneRef.current = true;
      if (navigator.vibrate) navigator.vibrate(80);
      onResult(parsed);
    } else {
      setNotCfdi(true);
      setTimeout(() => setNotCfdi(false), 2500);
    }
  };

  useEffect(() => {
    let active = true;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();
        scanLoop();
      } catch {
        setCameraError(true);
      }
    };
    let lastScan = 0;
    const scanLoop = (ts = 0) => {
      if (!active || doneRef.current) return;
      rafRef.current = requestAnimationFrame(scanLoop);
      if (ts - lastScan < 180) return;
      lastScan = ts;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
      if (code?.data) handleText(code.data);
    };
    start();
    return () => {
      active = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDecoding(true);
    try {
      const bmp = await createImageBitmap(file);
      const canvas = document.createElement("canvas");
      const maxDim = 1600;
      const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
      canvas.width = Math.round(bmp.width * scale);
      canvas.height = Math.round(bmp.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(img.data, img.width, img.height);
      if (code?.data) handleText(code.data);
      else setNotCfdi(true);
    } catch {
      setNotCfdi(true);
    } finally {
      setDecoding(false);
      e.target.value = "";
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(5,8,18,0.92)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="qr-scanner-modal"
    >
      <div className="w-full max-w-md panel-strong overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// ESCANEAR QR DEL CFDI</div>
            <div className="text-xs text-slate-400 mt-0.5">Apunta al código QR de la factura</div>
          </div>
          <button type="button" onClick={onClose} data-testid="qr-scanner-close" className="btn-ghost p-1.5 rounded-sm">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative bg-black" style={{ aspectRatio: "4/3" }}>
          {cameraError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
              <Camera className="w-10 h-10 text-slate-600" />
              <p className="text-xs text-slate-400">
                No se pudo acceder a la cámara. Sube una foto del QR de la factura.
              </p>
            </div>
          ) : (
            <>
              <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="w-52 h-52 rounded-sm relative"
                  style={{ border: "2px solid var(--cyan)", boxShadow: "0 0 0 9999px rgba(5,8,18,0.45)" }}
                >
                  <ScanLine className="w-full absolute top-1/2 -translate-y-1/2 animate-pulse" style={{ color: "var(--cyan)", opacity: 0.6 }} />
                </div>
              </div>
            </>
          )}
          {notCfdi && (
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-sm text-[11px] font-semibold"
              style={{ background: "rgba(239,68,68,0.9)", color: "#fff" }}
              data-testid="qr-not-cfdi-warning"
            >
              No se detectó un QR de CFDI válido
            </div>
          )}
        </div>

        <div className="px-5 py-4 flex items-center justify-between gap-3">
          <p className="text-[10px] text-slate-500 flex-1">
            Se leerá automáticamente el UUID y el total de la factura.
          </p>
          <label className="btn-ghost px-3 py-2 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-2 cursor-pointer" data-testid="qr-upload-fallback">
            {decoding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageUp className="w-3.5 h-3.5" />}
            Subir foto
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
        </div>
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}
